import { z } from 'zod'
import { CriterionId, InstantieId, RevisieId, SectionId } from './id.js'
import { Moment, Person } from './moment.js'

/**
 * De twee lagen (R-171, R-172, U23).
 *
 * De invoerlaag is van de invuller, de toetslaag van de toetser. Ze staan in de
 * opslag los van elkaar, haaks op de aanvraag (R-172a), en de renderer weeft ze
 * in de weergave weer samen. Dat is precies waarom de aanvraag te bevriezen is
 * terwijl er aan de toetslaag nog wordt geschreven.
 *
 * Er is geen derde laag en er zijn geen rollen: wie wat mag volgt uit de deling
 * en het laag-eigenaarschap (U6, R-67, R-172), niet uit een rollenmodel in het
 * formulier.
 */
export const Laag = z.enum(['invoer', 'toets'])
export type Laag = z.infer<typeof Laag>

/**
 * Wat er in de toetslaag staat dat niet samen wordt bewerkt (ontwerp 4.7).
 *
 * De vrije tekst van de toetslaag, dus de opmerkingvelden die per criterium of
 * sectie zijn geconfigureerd, leeft in het toetsdocument van die sectie: een
 * eigen Yjs-document met de toetser als schrijver (6.8, M3). Wat hier blijft is
 * het losse record: de notitie of signalering die aan het dossier blijft hangen
 * (R-185), de adviesvraag bij een criterium (R-184), en de waarde van een
 * opmerkingveld dat bij terugleggen naar de indiener teruggaat (R-75).
 */
export const LaagEntry = z
  .strictObject({
    instantieId: InstantieId,
    laag: Laag,
    soort: z.enum(['notitie', 'signalering', 'adviesvraag', 'opmerking']),
    /** Waar het over gaat: een sectie of een criterium, en precies één van de twee. */
    sectieId: SectionId.optional(),
    criteriumId: CriterionId.optional(),
    tekst: z.string().min(1),
    persoon: Person,
    moment: Moment,
  })
  .superRefine((entry, ctx) => {
    const count = Number(entry.sectieId !== undefined) + Number(entry.criteriumId !== undefined)
    if (count !== 1) {
      ctx.addIssue({
        code: 'custom',
        path: ['sectieId'],
        message: 'een laag-entry hangt aan precies één sectie of één criterium',
      })
    }
  })
export type LaagEntry = z.infer<typeof LaagEntry>

/**
 * De uitkomst per criterium: wat de machine vaststelde en wat de mens ervan vond (R-152,
 * R-187, R-180, ontwerp 7.4).
 *
 * Eén record met twee benoemde delen, precies zoals 7.4 het zet. Beide zijn optioneel en
 * minstens één moet er staan: de runtime schrijft het machinedeel direct na het sluiten van de
 * invoerbeurt, en de toetser vult het menselijke deel later aan.
 *
 * **De twee delen zijn gegroepeerd en de kolommen zijn dat niet.** M4.1 besloot oordeel,
 * onderbouwing en toetser apart te houden, met R-152 en R-187 als grond en met R-114 erbij: een
 * aparte `toetser` maakt de geanonimiseerde weergave goedkoop en een jsonb-blob maakt hem
 * onbevraagbaar. Dat blijft zo. Wat hier verandert is de vorm van het type en niet de opslag:
 * `criterion_result` houdt een kolom per veld. De groepering bestaat omdat R-180 juist vraagt
 * dat de twee bronnen niet worden verward, en `uitkomst.oordeel?.voldoet` naast
 * `uitkomst.machine?.voldoet` is niet te verwarren waar twee platte velden dat wel zijn.
 *
 * Beide delen dragen hun eigen `revisieId`, en dat zijn bij een hertoets twee verschillende
 * standen: de controle draaide tegen de revisie die net bevroor, het oordeel rust op de stand
 * die de toetser voor zich had (R-175, R-178).
 */
/**
 * Wat een aangewezen automatische controle heeft vastgesteld (R-180, D-57, ontwerp 7.4).
 *
 * Aangeleverd en niet beslissend: R-149 is vervallen als instelling, dus het oordeel is altijd
 * van een mens. Wat hier staat spaart de toetser het nazoeken uit van wat machinaal al
 * vaststaat.
 *
 * `controleId` is de naam van de controle en niet zijn vorm; de catalogus staat in
 * `packages/engine/src/controles.ts`. `detail` is voor de toetser: hij noemt de velden waar het
 * aan ligt, en is leeg als de controle voldoet. `revisieId` is de revisie waartegen de controle
 * heeft gedraaid, en dat is een andere vraag dan de revisie waarop het menselijke oordeel rust:
 * bij een hertoets zijn dat twee verschillende standen.
 */
export const MachineOutcome = z.strictObject({
  controleId: z.string().min(1),
  voldoet: z.boolean(),
  detail: z.string(),
  revisieId: RevisieId,
  moment: Moment,
})
export type MachineOutcome = z.infer<typeof MachineOutcome>

/** Het menselijke oordeel: apart van de machine-uitkomst en altijd van een mens (R-149). */
export const Verdict = z.strictObject({
  /** Een cijfer bij een cijferbereik, een keuzewaarde bij een tweekeuze (R-148). */
  oordeel: z.union([z.number().int(), z.string().min(1)]),
  /**
   * Of dit oordeel het criterium afdoet, los van de waarde erboven (R-148, R-176).
   *
   * De oordeelschaal is instelbaar: een cijferbereik zoals 1 tot 9, of een tweekeuze zoals
   * voldoet of voldoet niet. Bij een cijferbereik zegt de waarde zelf niets over afdoen; een 6
   * uit 9 is niet af te leiden zonder te weten waar de grens ligt. Daarom staat het feit hier
   * apart in plaats van het uit de waarde te rekenen.
   */
  voldoet: z.boolean(),
  /** Verplicht, zonder uitzondering (R-147). */
  onderbouwing: z.string().min(1),
  toetser: Person,
  /** De stand waarop dit oordeel rust (R-175). */
  revisieId: RevisieId,
  moment: Moment,
})
export type Verdict = z.infer<typeof Verdict>

export const CriterionOutcome = z
  .strictObject({
    instantieId: InstantieId,
    /** De stabiele criterium-identiteit, extern refereerbaar (R-150). */
    criteriumId: CriterionId,
    /** Wat de aangewezen controle heeft vastgesteld, of niets (R-180). */
    machine: MachineOutcome.optional(),
    /** Wat de toetser ervan vindt, of niets zolang hij nog niet heeft geoordeeld. */
    oordeel: Verdict.optional(),
  })
  .superRefine((outcome, ctx) => {
    if (outcome.machine === undefined && outcome.oordeel === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['oordeel'],
        // Een uitkomst die geen van beide draagt zegt niets, en zou de werkverzameling van
        // R-176 vervuilen met een criterium waarover niemand iets heeft vastgesteld.
        message: 'een criteriumuitkomst draagt een machine-uitkomst, een oordeel, of beide',
      })
    }
  })
export type CriterionOutcome = z.infer<typeof CriterionOutcome>
